import React, { useState, useEffect, useCallback } from 'react';
import { 
  AlertTriangle, Package, Check, RefreshCw, X, ChevronRight,
  AlertCircle, Clock, Bell
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../context/BranchContext';

const LowStockAlerts = ({ isOpen, onClose }) => {
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState({ total: 0, unacknowledged: 0, critical: 0 });
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const navigate = useNavigate();
  const { currentBranch } = useBranch();

  const fetchAlerts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (currentBranch?.id) params.append('branch_id', currentBranch.id);
      params.append('acknowledged', 'false');
      
      const res = await api.get(`/inventory/low-stock-alerts?${params.toString()}`);
      setAlerts(res.data.alerts || []);
      setStats({
        total: res.data.total || 0,
        unacknowledged: res.data.unacknowledged || 0,
        critical: res.data.critical || 0
      });
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
    setLoading(false);
  }, [currentBranch]);

  useEffect(() => {
    if (isOpen) {
      fetchAlerts();
    }
  }, [isOpen, fetchAlerts]);

  // Auto-refresh every 30 seconds when open
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(fetchAlerts, 30000);
    return () => clearInterval(interval);
  }, [isOpen, fetchAlerts]);

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const params = currentBranch?.id ? `?branch_id=${currentBranch.id}` : '';
      const res = await api.post(`/inventory/low-stock-alerts/check${params}`);
      toast.success(res.data.message);
      fetchAlerts();
    } catch (err) {
      toast.error('Failed to check for low stock');
    }
    setChecking(false);
  };

  const handleAcknowledge = async (alertId, e) => {
    e.stopPropagation();
    try {
      await api.post(`/inventory/low-stock-alerts/${alertId}/acknowledge`);
      setAlerts(prev => prev.filter(a => a.id !== alertId));
      setStats(prev => ({
        ...prev,
        unacknowledged: Math.max(0, prev.unacknowledged - 1)
      }));
      toast.success('Alert acknowledged');
    } catch (err) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleAlertClick = (alert) => {
    navigate('/inventory');
    onClose();
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <div 
        className="absolute right-4 top-16 w-96 bg-white rounded-2xl shadow-2xl border overflow-hidden max-h-[80vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-red-50 to-orange-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Low Stock Alerts</h3>
                <p className="text-xs text-gray-500">
                  {stats.critical > 0 && <span className="text-red-600 font-medium">{stats.critical} critical</span>}
                  {stats.critical > 0 && stats.unacknowledged > stats.critical && ' • '}
                  {stats.unacknowledged > 0 && `${stats.unacknowledged} unread`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCheckNow}
                disabled={checking}
                className="p-2 hover:bg-white/50 rounded-lg transition-colors"
                title="Check now"
              >
                <RefreshCw className={`w-4 h-4 text-gray-600 ${checking ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/50 rounded-lg"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Alerts List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
              <p className="text-sm text-gray-500 mt-2">Checking stock levels...</p>
            </div>
          ) : alerts.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package className="w-8 h-8 text-green-600" />
              </div>
              <h4 className="font-medium text-gray-900">All Stock Healthy!</h4>
              <p className="text-sm text-gray-500 mt-1">No items below reorder level</p>
              <button
                onClick={handleCheckNow}
                disabled={checking}
                className="mt-4 px-4 py-2 bg-[#4A7C59] text-white text-sm rounded-lg hover:bg-[#3A6346] transition-colors inline-flex items-center gap-2"
              >
                <RefreshCw className={`w-4 h-4 ${checking ? 'animate-spin' : ''}`} />
                Check Now
              </button>
            </div>
          ) : (
            <div className="divide-y">
              {alerts.map(alert => (
                <div
                  key={alert.id}
                  className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                    alert.severity === 'critical' ? 'bg-red-50/50' : 'bg-orange-50/30'
                  }`}
                  onClick={() => handleAlertClick(alert)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        alert.severity === 'critical' 
                          ? 'bg-red-100' 
                          : 'bg-orange-100'
                      }`}>
                        {alert.severity === 'critical' ? (
                          <AlertCircle className="w-5 h-5 text-red-600" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-orange-600" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-gray-900">{alert.item_name}</p>
                          <p className="text-xs text-gray-500">{alert.category}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                          alert.severity === 'critical'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-orange-100 text-orange-700'
                        }`}>
                          {alert.severity === 'critical' ? 'OUT' : 'LOW'}
                        </span>
                      </div>
                      <div className="mt-2 flex items-center gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Current:</span>
                          <span className={`ml-1 font-medium ${
                            alert.current_quantity === 0 ? 'text-red-600' : 'text-orange-600'
                          }`}>
                            {alert.current_quantity} {alert.unit}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-500">Reorder:</span>
                          <span className="ml-1 font-medium text-gray-700">
                            {alert.reorder_level} {alert.unit}
                          </span>
                        </div>
                      </div>
                      {alert.branch_name && (
                        <p className="text-xs text-gray-400 mt-1">{alert.branch_name}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex flex-col items-end gap-2">
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(alert.created_at)}
                      </span>
                      <button
                        onClick={(e) => handleAcknowledge(alert.id, e)}
                        className="p-1.5 bg-white border rounded-lg hover:bg-gray-50 text-gray-500 hover:text-green-600 transition-colors"
                        title="Acknowledge"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {alerts.length > 0 && (
          <div className="p-3 border-t bg-gray-50">
            <button
              onClick={() => { navigate('/inventory'); onClose(); }}
              className="w-full py-2 text-sm text-[#4A7C59] hover:bg-white rounded-lg transition-colors flex items-center justify-center gap-1"
            >
              View All Inventory
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Alert Badge for Header - Shows count of critical alerts
export const LowStockAlertBadge = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [alertCount, setAlertCount] = useState(0);
  const [criticalCount, setCriticalCount] = useState(0);
  const { currentBranch } = useBranch();

  useEffect(() => {
    const fetchAlertCount = async () => {
      try {
        const params = new URLSearchParams();
        if (currentBranch?.id) params.append('branch_id', currentBranch.id);
        params.append('acknowledged', 'false');
        
        const res = await api.get(`/inventory/low-stock-alerts?${params.toString()}`);
        setAlertCount(res.data.unacknowledged || 0);
        setCriticalCount(res.data.critical || 0);
      } catch (err) {
        console.error('Error fetching alert count:', err);
      }
    };

    fetchAlertCount();
    // Poll for updates every 60 seconds
    const interval = setInterval(fetchAlertCount, 60000);
    return () => clearInterval(interval);
  }, [currentBranch]);

  if (alertCount === 0) return null;

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
        data-testid="low-stock-alert-badge"
      >
        <AlertTriangle className={`w-5 h-5 ${criticalCount > 0 ? 'text-red-500' : 'text-orange-500'}`} />
        <span className={`absolute -top-1 -right-1 w-5 h-5 text-white text-xs rounded-full flex items-center justify-center ${
          criticalCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-orange-500'
        }`}>
          {alertCount > 9 ? '9+' : alertCount}
        </span>
      </button>
      <LowStockAlerts isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default LowStockAlerts;
